// frontend/src/lib/core/request.ts
// Shared request wrapper used by all domain modules (CSP-safe, no eval).

import { ensureCsrf } from "../csrf";

export type ApiValidationErrors = Record<string, string[]>;

export class ApiError extends Error {
  status?: number;
  payload?: any;
  validation?: ApiValidationErrors; // Laravel 422 keyed errors

  constructor(message: string, status?: number, payload?: any, validation?: ApiValidationErrors) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
    this.validation = validation;
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** Read a cookie value safely (no regex injection). */
function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Read a meta tag's content (e.g. <meta name="csrf-token" ...>) */
function readMeta(name: string): string | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  return el?.content ?? null;
}

/** Extract Laravel validation errors if present. */
function extractValidation(data: any): ApiValidationErrors | undefined {
  const direct = data?.errors;
  if (direct && typeof direct === "object") return direct as ApiValidationErrors;
  const nested = data?.payload?.errors;
  if (nested && typeof nested === "object") return nested as ApiValidationErrors;
  return undefined;
}

/** Parse JSON if content-type is JSON and body exists. */
async function parseJsonSafely<T = unknown>(res: Response): Promise<T | undefined> {
  const ct = (res.headers.get("content-type") ?? "").toLowerCase();
  if (!ct.includes("application/json") && !ct.includes("application/problem+json")) {
    return undefined as unknown as T;
  }
  try {
    return (await res.json()) as T;
  } catch {
    return undefined as unknown as T;
  }
}

/** Build CSRF headers from cookie or meta tag (support both Sanctum & VerifyCsrfToken) */
function csrfHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const xsrfCookie = readCookie("XSRF-TOKEN"); // Sanctum sets this
  const metaToken = readMeta("csrf-token") || (globalThis as any).csrfToken || null;

  if (xsrfCookie) {
    // Sanctum expects this header (value = cookie)
    headers["X-XSRF-TOKEN"] = xsrfCookie;
  }
  if (metaToken) {
    // Some non-xapi web routes/middleware read this header name
    headers["X-CSRF-TOKEN"] = metaToken;
  }
  return headers;
}

// 🔧 Exported so callers can do: import { request } from '../lib/core/request'
export async function request<T>(
  url: string,
  opts: RequestInit & { method?: Method } = {}
): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase() as Method;

  // If unsafe method OR we don't have the XSRF cookie yet, hydrate CSRF first.
  const hasXsrf = !!readCookie("XSRF-TOKEN");
  if (!["GET", "HEAD", "OPTIONS"].includes(method) || !hasXsrf) {
    await ensureCsrf();
  }

  const buildHeaders = (): HeadersInit => {
    const base: Record<string, string> = {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...csrfHeaders(),
    };

    // JSON body => Content-Type; FormData must not set it
    if (opts.body && !(opts.body instanceof FormData)) {
      base["Content-Type"] = "application/json";
    }

    // Merge any caller-provided headers
    return { ...base, ...(opts.headers || {}) };
  };

  const doFetch = async (): Promise<Response> =>
    fetch(url, { credentials: "include", headers: buildHeaders(), ...opts });

  let res = await doFetch();

  // Retry once on CSRF/session issues after refreshing CSRF
  if (res.status === 419 || res.status === 401) {
    await ensureCsrf();
    res = await doFetch();
  }

  if (!res.ok) {
    // Try to parse JSON error payload (don’t throw on non-JSON)
    const data = await parseJsonSafely<any>(res);
    const message = (data?.message ?? data?.error ?? `HTTP ${res.status}`) as string;
    const validation = res.status === 422 ? extractValidation(data) : undefined;
    throw new ApiError(message, res.status, data, validation);
  }

  // No content
  if (res.status === 204 || res.status === 205) {
    return undefined as unknown as T;
  }

  // Prefer JSON when available; otherwise return undefined
  const json = await parseJsonSafely<T>(res);
  return (json ?? (undefined as unknown as T)) as T;
}

/** Verb helpers (named) */
export function apiGet<T>(url: string, init?: RequestInit) {
  return request<T>(url, { ...(init || {}), method: "GET" });
}
export function apiPost<T>(url: string, body?: any, init?: RequestInit) {
  const payload = body instanceof FormData ? body : JSON.stringify(body ?? {});
  return request<T>(url, { ...(init || {}), method: "POST", body: payload });
}
export function apiPut<T>(url: string, body?: any, init?: RequestInit) {
  const payload = body instanceof FormData ? body : JSON.stringify(body ?? {});
  return request<T>(url, { ...(init || {}), method: "PUT", body: payload });
}
export function apiPatch<T>(url: string, body?: any, init?: RequestInit) {
  const payload = body instanceof FormData ? body : JSON.stringify(body ?? {});
  return request<T>(url, { ...(init || {}), method: "PATCH", body: payload });
}
export function apiDelete<T>(url: string, init?: RequestInit) {
  return request<T>(url, { ...(init || {}), method: "DELETE" });
}

/** Back-compat alias */
export const apiDel = apiDelete;

/** Convenience object (some modules import `{ api }`) */
export const api = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  delete: apiDelete,
};

export default api;
